import React, { FC, useState } from 'react';
import iban from 'iban';
import { useTranslation } from 'react-i18next';
import Logo from '../../../../assets/img/sepa_payment_logo.svg';
import { Button, Box } from '@mui/material';
import CustomInput from '../../CustomInput';
import BaseModal from '../BaseModal/BaseModal';

type Props = {
  amount?: number;
  show: boolean;
  handleClose: () => void;
  handlePayment: ({
    iban,
    name,
    lastName,
    bank,
  }: {
    iban: string;
    name: string;
    lastName: string;
    bank: string;
  }) => void;
};

interface FormValues {
  iban: string;
  name: string;
  lastName: string;
  bank: string;
}

const PaymentModal: FC<Props> = ({ amount, show, handleClose, handlePayment }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormValues>({
    iban: '',
    name: '',
    lastName: '',
    bank: ''
  });
  const [displayIban, setDisplayIban] = useState<string>('');
  const [errors, setErrors] = useState<Partial<FormValues>>({});

  const formatIban = (value: string): string => {
    const cleanValue = value.replace(/\s/g, '').toUpperCase();
    return cleanValue.replace(/(.{4})/g, '$1 ').trim();
  };

  const cleanIban = (value: string): string => {
    return value.replace(/\s/g, '').toUpperCase();
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const field = event.target.name as keyof FormValues;
    
    if (field === 'iban') {    
      const inputValue = event.target.value;
      const cleanValue = cleanIban(inputValue);
      const formattedValue = formatIban(inputValue);
      
      // Limit to 34 characters (max IBAN length without spaces)
      if (cleanValue.length <= 34) {
        setFormData({ ...formData, [field]: cleanValue });
        setDisplayIban(formattedValue);
      }
    } else {
      setFormData({ ...formData, [field]: event.target.value });
    }
    
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleInputBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const field = event.target.name as keyof FormValues;
    const value = field === 'iban' ? formData.iban : formData[field];
    
    if (!value) {
      if (field === 'lastName') {
        setErrors({ ...errors, [field]: t('paymentModal.accountHolderFirstNameValidation') });
      } else if (field === 'name') {
        setErrors({ ...errors, [field]: t('paymentModal.accountHolderNameValidation') });
      } else if (field === 'iban') {
        setErrors({ ...errors, [field]: 'Bitte gültige IBAN eingeben' });
      } else if (field === 'bank') {
        setErrors({ ...errors, [field]: t('paymentModal.creditInstituteValidation') });
      }
    } else if (field === 'iban' && !iban.isValid(value)) {
      setErrors({ ...errors, [field]: 'Bitte gültige IBAN eingeben' });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<FormValues> = {};
    
    if (!formData.lastName) {
      newErrors.lastName = t('paymentModal.accountHolderFirstNameValidation');
    }
    if (!formData.name) {
      newErrors.name = t('paymentModal.accountHolderNameValidation');
    }
    if (!formData.iban) {
      newErrors.iban = 'Bitte gültige IBAN eingeben';
    } else if (!iban.isValid(formData.iban)) {
      newErrors.iban = 'Bitte gültige IBAN eingeben';
    }
    if (!formData.bank) {
      newErrors.bank = t('paymentModal.creditInstituteValidation');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (validateForm()) {
      handlePayment(formData);
    }
  };

  const handleModalClose = () => {
    // Reset form data
    setFormData({
      iban: '',
      name: '',
      lastName: '',
      bank: ''
    });
    setDisplayIban('');
    setErrors({});
    handleClose();
  };

  return (
    <BaseModal
      open={show}
      onClose={handleModalClose}
      title="SEPA DIRECT DEBIT"
      titleIcon={Logo}
      actions={
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleSubmit}
          sx={{ m: 2, mt: 0 }}
        >
          {amount ? `${amount.toFixed(2)} € bezahlen` : t('paymentModal.addNewPayment')}
        </Button>
      }
    >
      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
        <CustomInput
          required
          name="lastName"
          label={t('paymentModal.accountHolderFirstName')}
          value={formData.lastName}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          error={!!errors.lastName}
          helperText={errors.lastName || ''}
        />
        
        <CustomInput
          required
          name="name"
          label={t('paymentModal.accountHolderName')}
          value={formData.name}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          error={!!errors.name}
          helperText={errors.name || ''}
        />
        
        <CustomInput
          required
          name="iban"
          label="IBAN"
          value={displayIban}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          error={!!errors.iban}
          helperText={errors.iban || ''}
        />
        
        <CustomInput
          required
          name="bank"
          label={t('paymentModal.creditInstitute')}
          value={formData.bank}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          error={!!errors.bank}
          helperText={errors.bank || ''}
        />
      </Box>
    </BaseModal>
  );
};


export default PaymentModal;
